import { useState, useEffect, useMemo } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { colors, Header, Card } from '../shared';

// ─── Drill-down Modal ───────────────────────────────────────────────────────

const DrillDownModal = ({ title, techName, onClose, children }) => (
  <div
    className="absolute inset-0 z-50 flex flex-col"
    style={{ background: colors.black }}
  >
    <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: colors.border }}>
      <div>
        <h3 className="font-semibold text-sm" style={{ color: colors.textPrimary }}>{title}</h3>
        <p className="text-xs" style={{ color: colors.textMuted }}>{techName}</p>
      </div>
      <button
        onClick={onClose}
        className="p-2 rounded-lg"
        style={{ background: colors.cardBg, color: colors.textMuted }}
      >
        <X size={18} />
      </button>
    </div>
    <div className="flex-1 overflow-y-auto p-4 pb-20">
      {children}
    </div>
  </div>
);

const LaborDrillDown = ({ techFullName, laborEntries, lookbackDays, onClose }) => {
  const cutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - lookbackDays);
    return d;
  }, [lookbackDays]);

  const techLabor = useMemo(() => {
    const techNameLower = techFullName?.toLowerCase();
    return laborEntries
      .filter(entry => {
        if (entry.techName?.toLowerCase() !== techNameLower) return false;
        const dateVal = entry.date || entry.createdDate;
        if (!dateVal) return false;
        const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
        return d >= cutoff && d <= new Date();
      })
      .sort((a, b) => {
        const da = a.date ? new Date(a.date) : new Date(0);
        const db = b.date ? new Date(b.date) : new Date(0);
        return db - da;
      });
  }, [techFullName, laborEntries, cutoff]);

  const totalHours = techLabor.reduce((sum, e) => sum + (e.hours || 0), 0);

  return (
    <DrillDownModal
      title={`Time Entries (${lookbackDays}d)`}
      techName={`${techFullName} — ${Math.round(totalHours * 10) / 10} hrs total`}
      onClose={onClose}
    >
      {techLabor.length === 0 ? (
        <p className="text-sm" style={{ color: colors.textMuted }}>No labor entries found.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: colors.border }}>
              <th className="text-left py-2 pr-2 font-medium" style={{ color: colors.textMuted }}>WO #</th>
              <th className="text-right py-2 px-2 font-medium" style={{ color: colors.textMuted }}>Hours</th>
              <th className="text-left py-2 pl-2 font-medium" style={{ color: colors.textMuted }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {techLabor.map((entry, i) => (
              <tr key={entry.id || i} className="border-b" style={{ borderColor: colors.border }}>
                <td className="py-2 pr-2" style={{ color: colors.textSecondary }}>
                  <div>{entry.woNumber || '—'}</div>
                  <div className="text-xs" style={{ color: colors.textMuted }}>
                    {entry.date ? new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  </div>
                </td>
                <td className="py-2 px-2 text-right font-mono" style={{ color: colors.yellow }}>
                  {entry.hours}
                </td>
                <td className="py-2 pl-2 text-xs" style={{ color: colors.textSecondary }}>
                  {entry.description || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </DrillDownModal>
  );
};

const WOsDrillDown = ({ techFullName, completedWOs, lookbackDays, onClose }) => {
  const sorted = useMemo(() =>
    [...completedWOs].sort((a, b) => {
      const da = a.completedOn ? new Date(a.completedOn) : new Date(0);
      const db = b.completedOn ? new Date(b.completedOn) : new Date(0);
      return db - da;
    }),
    [completedWOs]
  );

  return (
    <DrillDownModal
      title={`Done WOs (${lookbackDays}d)`}
      techName={`${techFullName} — ${sorted.length} WOs`}
      onClose={onClose}
    >
      {sorted.length === 0 ? (
        <p className="text-sm" style={{ color: colors.textMuted }}>No completed work orders found.</p>
      ) : (
        <div className="space-y-2">
          {sorted.map((wo, i) => (
            <div
              key={wo.id || i}
              className="p-3 rounded-xl"
              style={{ background: colors.cardBg }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold" style={{ color: colors.textPrimary }}>
                  WO #{wo.woNumber || '—'}
                </span>
                <span className="text-xs" style={{ color: colors.textMuted }}>
                  {wo.completedOn ? new Date(wo.completedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                </span>
              </div>
              <div className="text-xs mb-1" style={{ color: colors.textMuted }}>
                {wo.property || '—'}{wo.unit ? ` · Unit ${wo.unit}` : ''}
              </div>
              {wo.description && (
                <p className="text-xs" style={{ color: colors.textSecondary }}>{wo.description}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </DrillDownModal>
  );
};

const SurveyDrillDown = ({ techFullName, satisfactionScores, lookbackDays, onClose }) => {
  const cutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - lookbackDays);
    return d;
  }, [lookbackDays]);

  const techScores = useMemo(() => {
    const techNameLower = techFullName?.toLowerCase();
    return satisfactionScores
      .filter(s => {
        if (!s.techName?.toLowerCase().includes(techNameLower)) return false;
        const scoreDate = s.date || s.dateReceived;
        if (scoreDate) {
          const d = scoreDate.toDate ? scoreDate.toDate() : new Date(scoreDate);
          return d >= cutoff && d <= new Date();
        }
        return true;
      })
      .sort((a, b) => {
        const da = a.date ? new Date(a.date) : new Date(0);
        const db = b.date ? new Date(b.date) : new Date(0);
        return db - da;
      });
  }, [techFullName, satisfactionScores, cutoff]);

  const avgScore = techScores.length > 0
    ? Math.round((techScores.reduce((sum, s) => sum + (s.score || 0), 0) / techScores.length) * 10) / 10
    : 0;

  const getScoreColor = (score) => {
    if (score >= 5) return colors.green;
    if (score >= 4) return colors.yellow;
    if (score >= 3) return colors.orange;
    return colors.red;
  };

  return (
    <DrillDownModal
      title={`Survey Results (${lookbackDays}d)`}
      techName={`${techFullName} — ${avgScore} avg (${techScores.length} surveys)`}
      onClose={onClose}
    >
      {techScores.length === 0 ? (
        <p className="text-sm" style={{ color: colors.textMuted }}>No survey results found.</p>
      ) : (
        <div className="space-y-3">
          {techScores.map((s, i) => (
            <Card key={s.id || i} className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-lg font-bold" style={{ color: getScoreColor(s.score) }}>
                  {s.score}/5
                </span>
                <span className="text-xs" style={{ color: colors.textMuted }}>
                  {s.date ? new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No date'}
                </span>
              </div>
              {s.woNumber && (
                <p className="text-xs mb-1" style={{ color: colors.textSecondary }}>WO #{s.woNumber}</p>
              )}
              {s.feedback && (
                <p className="text-sm mt-2" style={{ color: colors.textPrimary }}>"{s.feedback}"</p>
              )}
              {!s.feedback && (
                <p className="text-xs italic" style={{ color: colors.textMuted }}>No commentary</p>
              )}
            </Card>
          ))}
        </div>
      )}
    </DrillDownModal>
  );
};

// ─── Main HomeScreen ────────────────────────────────────────────────────────

const HomeScreen = ({ setActive }) => {
  const { user, logout } = useAuth();
  const {
    getCurrentTech,
    getOpenWOsForTech,
    getLeaderboardData,
    laborEntries,
    satisfactionScores,
    refreshDataFromStorage,
    refreshing,
    lastRefresh,
  } = useData();

  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState(7);
  const [sortBy, setSortBy] = useState('wos');
  const [drillDown, setDrillDown] = useState(null); // { type: 'wos'|'hours'|'satisfaction', techFullName, completedWOs? }

  const currentTech = getCurrentTech();
  const openWOs = getOpenWOsForTech();

  useEffect(() => {
    const data = getLeaderboardData(leaderboardPeriod);
    const sorted = [...data].sort((a, b) => b[sortBy] - a[sortBy]);
    setLeaderboardData(sorted);
  }, [getLeaderboardData, leaderboardPeriod, sortBy]);

  const handleRefresh = async () => {
    await refreshDataFromStorage();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="h-full overflow-y-auto pb-20 relative" style={{ background: colors.black }}>
      <Header
        title="Trades Beam"
        subtitle={`${getGreeting()}, ${currentTech?.name || user?.displayName?.split(' ')[0] || 'Tech'}`}
        rightAction={
          <button
            onClick={logout}
            className="text-xs px-3 py-1 rounded-lg"
            style={{ background: colors.cardBg, color: colors.textMuted }}
          >
            Sign out
          </button>
        }
      />

      <div className="px-4">
        {/* Personal Overview */}
        <Card className="mb-4">
          <div className="flex justify-between items-center mb-3">
            <span className="font-semibold" style={{ color: colors.textPrimary }}>Your Overview</span>
            <span className="text-sm" style={{ color: colors.textMuted }}>
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
          <button
            onClick={() => setActive('open-wos')}
            className="text-center w-full p-3 rounded-xl transition-all active:scale-95"
            style={{ background: colors.yellowDim }}
          >
            <p className="text-2xl font-bold" style={{ color: colors.yellow }}>{openWOs.length}</p>
            <p className="text-xs" style={{ color: colors.textSecondary }}>Open WOs</p>
          </button>
        </Card>

        {/* Leaderboard */}
        <Card>
          <div className="flex justify-between items-center mb-4">
            <span className="font-semibold" style={{ color: colors.textPrimary }}>Leaderboard</span>
            <div className="flex gap-1">
              {[7, 30, 90].map(days => (
                <button
                  key={days}
                  onClick={() => setLeaderboardPeriod(days)}
                  className="text-xs px-2 py-1 rounded-lg transition-all"
                  style={{
                    background: leaderboardPeriod === days ? colors.yellowDim : colors.inputBg,
                    color: leaderboardPeriod === days ? colors.yellow : colors.textMuted,
                  }}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>

          {/* Header Row */}
          <div className="flex items-center pb-3 mb-3 border-b" style={{ borderColor: colors.border }}>
            <div className="flex-1 min-w-0 pl-2"><span className="text-xs font-medium" style={{ color: colors.textMuted }}>Tech</span></div>
            {[
              { key: 'wos', icon: '\u2705', title: 'Done WOs' },
              { key: 'hours', icon: '\u{1F553}', title: 'Worked Hours' },
              { key: 'satisfaction', icon: '\u2B50', title: 'Survey Stars' },
            ].map(col => (
              <button
                key={col.key}
                onClick={() => setSortBy(col.key)}
                className="w-14 text-center shrink-0 py-1 rounded-lg transition-all"
                style={{
                  background: sortBy === col.key ? colors.yellowDim : 'transparent',
                }}
                title={`Sort by ${col.title}`}
              >
                <span style={{ opacity: sortBy === col.key ? 1 : 0.6 }}>{col.icon}</span>
              </button>
            ))}
          </div>

          {/* Data Rows */}
          <div className="space-y-1">
            {leaderboardData.map((tech) => {
              const isCurrentUser = tech.fullName?.toLowerCase() === currentTech?.name?.toLowerCase();

              return (
                <div
                  key={tech.fullName}
                  className="flex items-center py-2 px-1 rounded-xl"
                  style={{
                    background: isCurrentUser ? colors.yellowDim : 'transparent',
                    border: isCurrentUser ? `1px solid ${colors.yellow}` : 'none',
                  }}
                >
                  <div className="flex-1 min-w-0 px-2">
                    <p className="font-medium text-sm truncate" style={{ color: colors.textPrimary }}>
                      {tech.name}
                    </p>
                    {tech.location && (
                      <p className="text-xs truncate" style={{
                        color: tech.location.status === 'clocked-in' ? colors.green
                             : tech.location.status === 'recent' ? colors.textSecondary
                             : colors.textMuted,
                      }}>
                        {tech.location.status === 'clocked-in'
                          ? tech.location.propertyName || 'On site'
                          : tech.location.status === 'recent'
                            ? `${tech.location.propertyName || 'Off site'} · ${tech.location.ago}`
                            : `Last seen ${tech.location.ago}`}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setDrillDown({ type: 'wos', techFullName: tech.fullName, completedWOs: tech.completedWOs })}
                    className="w-14 text-center shrink-0 rounded-lg py-1 transition-all active:scale-95"
                    style={{ background: 'transparent' }}
                    title="View completed WOs"
                  >
                    <span className="text-sm font-semibold" style={{
                      color: sortBy === 'wos' ? colors.yellow : colors.textPrimary,
                      borderBottom: `1px dashed ${colors.textMuted}`,
                    }}>{tech.wos}</span>
                  </button>
                  <button
                    onClick={() => setDrillDown({ type: 'hours', techFullName: tech.fullName })}
                    className="w-14 text-center shrink-0 rounded-lg py-1 transition-all active:scale-95"
                    style={{ background: 'transparent' }}
                    title="View time entries"
                  >
                    <span className="text-sm font-semibold" style={{
                      color: sortBy === 'hours' ? colors.yellow : colors.textPrimary,
                      borderBottom: `1px dashed ${colors.textMuted}`,
                    }}>{tech.hours}</span>
                  </button>
                  <button
                    onClick={() => setDrillDown({ type: 'satisfaction', techFullName: tech.fullName })}
                    className="w-14 text-center shrink-0 rounded-lg py-1 transition-all active:scale-95"
                    style={{ background: 'transparent' }}
                    title="View survey results"
                  >
                    <span
                      className="text-sm font-bold"
                      style={{
                        color: sortBy === 'satisfaction' ? colors.yellow :
                               tech.satisfaction >= 4.7 ? colors.green :
                               tech.satisfaction >= 4.5 ? colors.yellow :
                               tech.satisfaction > 0 ? colors.orange : colors.textMuted,
                        borderBottom: `1px dashed ${colors.textMuted}`,
                      }}
                    >
                      {tech.satisfaction || '-'}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="w-full mt-4 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all active:scale-98 disabled:opacity-50"
          style={{ background: colors.cardBg, color: colors.textSecondary, border: `1px solid ${colors.border}` }}
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </button>

        {lastRefresh && (
          <p className="text-xs text-center mt-2" style={{ color: colors.textMuted }}>
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        )}
      </div>

      {/* Drill-down overlays */}
      {drillDown?.type === 'wos' && (
        <WOsDrillDown
          techFullName={drillDown.techFullName}
          completedWOs={drillDown.completedWOs || []}
          lookbackDays={leaderboardPeriod}
          onClose={() => setDrillDown(null)}
        />
      )}
      {drillDown?.type === 'hours' && (
        <LaborDrillDown
          techFullName={drillDown.techFullName}
          laborEntries={laborEntries}
          lookbackDays={leaderboardPeriod}
          onClose={() => setDrillDown(null)}
        />
      )}
      {drillDown?.type === 'satisfaction' && (
        <SurveyDrillDown
          techFullName={drillDown.techFullName}
          satisfactionScores={satisfactionScores}
          lookbackDays={leaderboardPeriod}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
};

export default HomeScreen;
